import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { StatusBadge } from "@/components/StatusBadge";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "sonner";
import {
  ArrowLeft, FileDown, Save, Link2, Share2, ShieldOff, Clock, FileText,
  Image as ImageIcon, Bug, AlertTriangle, ClipboardList, Star, RefreshCw, Eye,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList } from "recharts";

const fmtBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"]; let v = n, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtDate = (s) => s ? new Date(s).toLocaleString() : "—";
const fileIcon = (mime) =>
  (mime || "").startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 text-cyan-600"/> : <FileText className="w-3.5 h-3.5 text-slate-500"/>;

export default function TaskReport() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [auto, setAuto] = useState(null);
  const [report, setReport] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [form, setForm] = useState({ summary: "", narrative_html: "", recommendations: "", in_scope: "", out_of_scope: "", client_visible: true });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${id}/report`);
      setAuto(data.auto);
      setReport(data.report);
      setCanEdit(!!data.can_edit);
      if (data.report) {
        setForm({
          summary: data.report.summary || "",
          narrative_html: data.report.narrative_html || "",
          recommendations: data.report.recommendations || "",
          in_scope: data.report.in_scope || "",
          out_of_scope: data.report.out_of_scope || "",
          client_visible: data.report.client_visible !== false,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load report");
      if (err.response?.status === 409) nav(`/tasks/${id}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/tasks/${id}/report`, form);
      setReport(data.report);
      toast.success("Report saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/tasks/${id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `task_report_${id.slice(0,8)}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate PDF");
    }
  };

  const issueShareLink = async () => {
    try {
      const { data } = await api.post(`/tasks/${id}/report/share`);
      setReport((r) => ({ ...(r || {}), share_token: data.token, share_expires_at: data.expires_at }));
      const url = `${window.location.origin}/shared/report/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Share link copied to clipboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create link");
    }
  };

  const revokeShareLink = async () => {
    if (!window.confirm("Revoke this share link? The client URL will stop working.")) return;
    try {
      await api.delete(`/tasks/${id}/report/share`);
      setReport((r) => ({ ...(r || {}), share_token: null, share_expires_at: null }));
      toast.success("Share link revoked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  if (loading || !auto) {
    return (
      <AppLayout title="Task report" subtitle="Loading…">
        <div className="text-sm text-slate-500">Loading task report…</div>
      </AppLayout>
    );
  }

  const t = auto.task;
  const shareUrl = report?.share_token ? `${window.location.origin}/shared/report/${report.share_token}` : null;

  return (
    <AppLayout title="Task report" subtitle={t.title}>
      <button onClick={() => nav(`/tasks/${id}`)} className="text-sm text-slate-500 hover:text-slate-900 mb-3 flex items-center gap-1.5" data-testid="btn-back-task">
        <ArrowLeft className="w-4 h-4"/> Back to task
      </button>

      <div className="flex flex-wrap items-center gap-2 justify-between mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge kind="task" value={t.status}/>
          <span className="text-sm text-slate-500">Project:</span>
          <Link to={`/projects/${auto.project.id}`} className="text-sm font-medium text-slate-900 hover:text-cyan-700">{auto.project.name}</Link>
          <span className="text-xs text-slate-400">· {auto.project.client_name || "Internal"}</span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-save-report">
              <Save className="w-4 h-4 mr-1.5"/>{busy ? "Saving…" : "Save"}
            </Button>
          )}
          <Button onClick={downloadPdf} variant="outline" className="border-slate-300" data-testid="btn-download-pdf">
            <FileDown className="w-4 h-4 mr-1.5"/>Download PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ---------- LEFT — Auto-captured data ---------- */}
        <div className="lg:col-span-1 space-y-4">
          <div className="kpi-card" data-testid="report-task-facts">
            <div className="text-[11px] uppercase text-slate-500 mb-2">Task facts</div>
            <Fact label="Task name" value={t.title}/>
            <Fact label="Environment" value={t.test_environment || "—"}/>
            <Fact label="Number of testers" value={auto.tester?.name ? `1 · ${auto.tester.name}` : "1"}/>
          </div>

          {auto.evidence_files?.length > 0 && (
            <div className="kpi-card" data-testid="report-evidence">
              <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5"/> Evidence files ({auto.evidence_files.length})
              </div>
              <ul className="space-y-1.5">
                {auto.evidence_files.map(f => (
                  <li key={f.id} className="flex items-center justify-between text-xs">
                    <a href={`${API}/files/${f.id}`} target="_blank" rel="noreferrer" className="text-slate-700 hover:text-cyan-700 truncate flex items-center gap-1.5" data-testid={`evidence-${f.id}`}>
                      {fileIcon(f.mime)} <span className="truncate">{f.name}</span>
                    </a>
                    <span className="text-slate-400 ml-2">{fmtBytes(f.size)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {auto.decline_history?.length > 0 && (
            <div className="kpi-card" data-testid="report-declines">
              <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600"/> Decline history ({auto.decline_history.length})
              </div>
              <ul className="space-y-1 text-[11px] text-slate-600">
                {auto.decline_history.map((d, idx) => (
                  <li key={idx} className="flex items-start gap-1.5">
                    <Clock className="w-3 h-3 mt-0.5 text-slate-400"/>
                    <span>{fmtDate(d.at)} — {d.reason || "(no reason given)"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {auto.ratings?.length > 0 && (
            <div className="kpi-card" data-testid="report-ratings">
              <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500"/> Manager ratings</div>
              <ul className="space-y-1.5">
                {auto.ratings.map(r => (
                  <li key={r.id || r.task_id + r.manager_id} className="text-xs text-slate-600">
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map(n => (
                        <Star key={n} className={`w-3 h-3 ${n <= r.rating ? "text-amber-500" : "text-slate-300"}`} fill={n <= r.rating ? "currentColor" : "none"}/>
                      ))}
                      <span className="text-[11px] text-slate-500 ml-1">{fmtDate(r.updated_at)}</span>
                    </div>
                    {r.notes && <div className="text-[11px] text-slate-500 mt-0.5">{r.notes}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ---------- RIGHT — Editor ---------- */}
        <div className="lg:col-span-2 space-y-4">
          <div className="kpi-card" data-testid="report-editor-panel">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display font-semibold text-slate-900">Report narrative</div>
                <div className="text-xs text-slate-500">Compile a sharable, client-facing write-up. The tester's submission and linked bugs are auto-attached on download / share.</div>
              </div>
              <button type="button" onClick={load} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="btn-reload-report"><RefreshCw className="w-3.5 h-3.5"/> Reload</button>
            </div>

            <div className="mb-3">
              <Label htmlFor="summary">Executive summary</Label>
              <Textarea id="summary" rows={3} maxLength={2000}
                placeholder="One-paragraph headline the client will read first."
                value={form.summary}
                onChange={(e) => setForm(f => ({ ...f, summary: e.target.value }))}
                disabled={!canEdit}
                data-testid="input-summary"
              />
            </div>

            <div className="mb-3">
              <Label>Findings & narrative</Label>
              <RichTextEditor
                value={form.narrative_html}
                onChange={(html) => setForm(f => ({ ...f, narrative_html: html }))}
                placeholder="Describe what was tested, what was found, screenshots, and verdict…"
                minHeight={320}
                disabled={!canEdit}
                testid="editor-narrative"
              />
              <div className="text-[11px] text-slate-400 mt-1">Tip: paste links and they auto-format. Use the toolbar for headings, lists, code and quotes.</div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div>
                <Label htmlFor="in-scope">In scope</Label>
                <Textarea id="in-scope" rows={4} maxLength={5000}
                  placeholder="Modules / flows / platforms explicitly covered by this test cycle."
                  value={form.in_scope}
                  onChange={(e) => setForm(f => ({ ...f, in_scope: e.target.value }))}
                  disabled={!canEdit}
                  data-testid="input-in-scope"
                />
              </div>
              <div>
                <Label htmlFor="out-scope">Out of scope</Label>
                <Textarea id="out-scope" rows={4} maxLength={5000}
                  placeholder="Anything deliberately not tested — deferred features, integrations, environments…"
                  value={form.out_of_scope}
                  onChange={(e) => setForm(f => ({ ...f, out_of_scope: e.target.value }))}
                  disabled={!canEdit}
                  data-testid="input-out-scope"
                />
              </div>
            </div>

            <div className="mb-3">
              <Label htmlFor="recommendations">Recommendations / next steps</Label>
              <Textarea id="recommendations" rows={3} maxLength={5000}
                placeholder="What should the client do next? Re-test on additional devices, fix verification window, etc."
                value={form.recommendations}
                onChange={(e) => setForm(f => ({ ...f, recommendations: e.target.value }))}
                disabled={!canEdit}
                data-testid="input-recommendations"
              />
            </div>

            {canEdit && (
              <div className="flex items-center gap-3 pt-2 border-t border-slate-100">
                <Switch
                  checked={form.client_visible}
                  onCheckedChange={(v) => setForm(f => ({ ...f, client_visible: v }))}
                  data-testid="switch-client-visible"
                />
                <Label className="text-sm">Client-visible (controls whether the public share link works)</Label>
              </div>
            )}
          </div>

          <DefectsTable issues={auto.issues || []}/>
          <CoverageCharts coverage={auto.coverage || {}}/>

          {/* ---- Share box ---- */}
          {canEdit && (
            <div className="kpi-card" data-testid="report-share-panel">
              <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5"/> Share with client</div>
              {shareUrl ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Input value={shareUrl} readOnly onFocus={(e)=>e.target.select()} className="font-mono text-xs" data-testid="share-link-input"/>
                    <Button variant="outline" onClick={()=>{navigator.clipboard.writeText(shareUrl); toast.success("Copied");}} className="border-slate-300" data-testid="btn-copy-share">
                      <Link2 className="w-3.5 h-3.5"/>
                    </Button>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <span>Expires {fmtDate(report.share_expires_at)}</span>
                    <button onClick={revokeShareLink} className="text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="btn-revoke-share">
                      <ShieldOff className="w-3 h-3"/> Revoke
                    </button>
                  </div>
                </div>
              ) : (
                <Button onClick={issueShareLink} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-create-share">
                  <Share2 className="w-4 h-4 mr-1.5"/> Generate public share link
                </Button>
              )}
              <p className="text-[11px] text-slate-500 mt-2">Anyone with the link can view the report (and download the PDF). 30-day TTL. Toggle "Client-visible" off to disable temporarily.</p>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

function Fact({ label, value, sub }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-xs font-medium text-slate-900 text-right max-w-[60%] truncate" title={value || "—"}>
        {value || "—"}
        {sub && <div className="text-[10px] text-slate-400 font-normal">{sub}</div>}
      </div>
    </div>
  );
}

const SEV_COLOR = {
  critical: "bg-red-100 text-red-800 border-red-200",
  high: "bg-orange-100 text-orange-800 border-orange-200",
  medium: "bg-amber-100 text-amber-800 border-amber-200",
  low: "bg-slate-100 text-slate-700 border-slate-200",
};

function DefectsTable({ issues }) {
  if (!issues || issues.length === 0) return null;
  const sevOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...issues].sort((a, b) =>
    (sevOrder[a.severity] ?? 9) - (sevOrder[b.severity] ?? 9)
  );
  return (
    <div className="kpi-card" data-testid="report-defects-table">
      <div className="flex items-center justify-between mb-2">
        <div className="font-display font-semibold text-slate-900 flex items-center gap-1.5">
          <Bug className="w-4 h-4 text-slate-500"/> Defects summary
        </div>
        <div className="text-xs text-slate-500">{sorted.length} total</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left p-2 w-24">ID</th>
              <th className="text-left p-2 w-24">Severity</th>
              <th className="text-left p-2">Title</th>
              <th className="text-left p-2 w-24">Status</th>
              <th className="text-right p-2 w-16">View</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(i => (
              <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`defect-row-${i.id}`}>
                <td className="p-2 font-mono text-slate-500">BUG-{i.id.slice(0,6).toUpperCase()}</td>
                <td className="p-2">
                  <span className={`inline-block px-2 py-0.5 rounded border text-[10px] uppercase ${SEV_COLOR[i.severity] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {i.severity || "—"}
                  </span>
                </td>
                <td className="p-2">
                  <Link to={`/issues/${i.id}`} className="font-medium text-slate-900 hover:text-cyan-700">{i.title}</Link>
                  {i.description && <div className="text-[11px] text-slate-500 line-clamp-2 mt-0.5">{i.description}</div>}
                </td>
                <td className="p-2 text-slate-600">{i.status || "—"}</td>
                <td className="p-2 text-right">
                  <Link to={`/issues/${i.id}`} className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900" data-testid={`defect-view-${i.id}`}>
                    <Eye className="w-3.5 h-3.5"/>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const COVERAGE_COLORS = ["#0891b2", "#0e7490", "#155e75", "#0369a1", "#1e40af", "#4338ca", "#6d28d9"];

function CoverageCharts({ coverage }) {
  const { devices = [], os = [], total_issues = 0 } = coverage || {};
  if (!total_issues) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="report-coverage">
      <CoverageChart title="Device coverage" testid="chart-devices" data={devices}/>
      <CoverageChart title="OS coverage" testid="chart-os" data={os}/>
    </div>
  );
}

function CoverageChart({ title, data, testid }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="font-display font-semibold text-slate-900 mb-2">{title}</div>
      <ResponsiveContainer width="100%" height={Math.max(140, data.length * 34)}>
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 30, left: 12, bottom: 4 }}>
          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} stroke="#94a3b8"/>
          <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} stroke="#334155"/>
          <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} contentStyle={{ fontSize: 12 }}/>
          <Bar dataKey="count" radius={[0, 4, 4, 0]}>
            <LabelList dataKey="count" position="right" style={{ fontSize: 11, fill: "#334155" }}/>
            {data.map((entry, idx) => (
              <Cell key={entry.name} fill={COVERAGE_COLORS[idx % COVERAGE_COLORS.length]}/>
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
